import React from 'react';
import { StockScreenerData } from './columnDefinitions';
import { fmtSignedPct } from '../../utils/screenerCard';
import { fmtPlainDate } from '../../utils/plainDate';
import { NORMAL_CC_WINDOW_DAYS } from '../../utils/earnings';
import LatestPriceChip from '../common/LatestPriceChip';
import { LatestStockQuote } from '../../types/latestPrice';

/**
 * Screener table view (desktop/tablet): one row per stock, every metric in its own
 * sortable column, for side-by-side comparison. The card answers "should I look at
 * this one?"; the table answers "which of these is best on X?".
 *
 * Sorting is server-side; every value is the backend's (gold phase 2b) for the
 * lens requested, so one sort key per column serves both CC and CSP.
 */

interface Column {
  id: string;
  label: string;
  sub?: string;
  /** Server sort key. Omit for display-only columns. */
  sortKey?: string;
  text?: boolean;
  render: (s: StockScreenerData, ctx: { isCsp: boolean; latest?: LatestStockQuote | null }) => React.ReactNode;
}

const pct = (v?: number | null, d = 1) => (v != null && !isNaN(v) ? `${v.toFixed(d)}%` : '—');
/** Score as a plain figure (trader theme): no coloured pill, the column header carries the scale. */
const score = (v?: number | null) => (v != null && !isNaN(v) ? <b className="st-score">{Math.round(v)}</b> : '—');
const pnl = (v?: number | null) =>
  v != null && !isNaN(v) ? <span className={v >= 0 ? 'sc2-pos' : 'sc2-neg'}>{fmtSignedPct(v)}</span> : '—';

const COLUMNS: Column[] = [
  { id: 'symbol', label: 'Symbol', sortKey: 'symbol', text: true, render: (s) => s.symbol },
  { id: 'name', label: 'Company', sortKey: 'company_name', text: true, render: (s) => s.company_name ?? '—' },
  {
    id: 'price', label: 'Price', sortKey: 'current_price',
    render: (s, { latest }) => (
      <>
        {s.current_price != null ? `$${s.current_price.toFixed(2)}` : '—'}
        {latest?.latest_price != null && (
          <LatestPriceChip className="st-latest" session={latest.bar_session} barTime={latest.bar_time} delayedMinutes={latest.delayed_minutes} price={latest.latest_price} changePct={latest.change_pct} />
        )}
      </>
    ),
  },
  { id: 'score', label: 'Score', sub: '/100', sortKey: 'score', render: (s) => score(s.score) },
  { id: 'fit', label: 'Income Fit', sub: '/50', sortKey: 'income_fit', render: (s) => score(s.income_fit) },
  { id: 'timing', label: 'Timing', sub: '/50', sortKey: 'timing', render: (s) => score(s.timing) },
  // One basis everywhere: premium yield per year of the best contract (glossary premium_yield)
  { id: 'yield', label: 'Premium', sub: '/ yr', sortKey: 'best_ann_yield', render: (s) => pct(s.best_ann_yield, 1) },
  { id: 'dte', label: 'DTE', sortKey: 'best_dte', render: (s) => (s.best_dte != null ? s.best_dte : '—') },
  { id: 'baseline', label: 'Usual result', sub: 'per 35 days', sortKey: 'baseline_pnl', render: (s) => pnl(s.baseline_pnl) },
  {
    id: 'setup', label: 'In this setup', sub: 'per 35 days', sortKey: 'setup_pnl',
    render: (s) => {
      if (s.setup_pnl == null) return '—';
      return <>{pnl(s.setup_pnl)}{s.setup_basis === 'cluster' && <span className="sc2-muted"> · peers</span>}</>;
    },
  },
  {
    id: 'spread', label: 'Spread', sortKey: 'spread_pct',
    render: (s) => {
      if (s.spread_pct == null) return '—';
      const out = s.liquidity?.reasons.includes('spread_above_max');
      return <span className={out ? 'is-flag' : undefined}>{pct(s.spread_pct, 0)}</span>;
    },
  },
  { id: 'ivr', label: 'IV rank', sortKey: 'iv_rank', render: (s) => (s.iv_rank != null ? <>{Math.round(s.iv_rank)}{s.iv_rank_interim && <span className="sc2-muted" title={`Interim: history since ${s.iv_rank_since}`}>*</span>}</> : '—') },
  {
    id: 'ivhv', label: 'IV vs moves', sub: 'implied / realized',
    render: (s) => (s.iv != null && s.hv != null ? `${s.iv.toFixed(0)} / ${s.hv.toFixed(0)}` : '—'),
  },
  {
    id: 'earnings', label: 'Earnings', sortKey: 'next_earnings_date',
    render: (s) => {
      const date = fmtPlainDate(s.next_earnings_date);
      const days = s.days_to_earnings;
      if (!date || days == null || days < 0) return '—';
      const cls = s.earnings_inside ? 'is-risk' : days <= NORMAL_CC_WINDOW_DAYS ? 'is-flag' : undefined;
      return <span className={cls}>{`${date} · ${days}d`}</span>;
    },
  },
  {
    id: 'margin', label: 'Net margin', sortKey: 'net_margin',
    render: (s) => {
      const v = s.net_margin;
      if (v == null) return '—';
      return <span className={v < 0 ? 'is-risk' : undefined}>{pct(v)}</span>;
    },
  },
  { id: 'div', label: 'Dividend', sortKey: 'dividend_yield', render: (s) => pct(s.dividend_yield) },
];

interface ScreenerTableProps {
  stocks: StockScreenerData[];
  /** Latest price (delayed) per symbol, shown as a chip under the Price. */
  latestQuotes?: Map<string, LatestStockQuote>;
  isCsp: boolean;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSort: (key: string) => void;
  onOpen: (stock: StockScreenerData) => void;
  isLoading: boolean;
  empty: React.ReactNode;
}

const ScreenerTable: React.FC<ScreenerTableProps> = ({ stocks, latestQuotes, isCsp, sortBy, sortDirection, onSort, onOpen, isLoading, empty }) => {
  const keyFor = (c: Column) => c.sortKey;
  // Older sort keys (cc_score / csp_score / cc_rank) all mean the score.
  const isActive = (c: Column) => {
    const k = keyFor(c);
    return k != null && (sortBy === k || (c.id === 'score' && ['cc_score', 'csp_score', 'cc_rank'].includes(sortBy)));
  };

  return (
    <div className="screener-table-wrap">
      <table className="screener-table">
        <thead>
          <tr>
            {COLUMNS.map((c) => {
              const k = keyFor(c);
              const active = isActive(c);
              const content = (
                <>
                  {c.label}
                  {active ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}
                  {c.sub && <small>{c.sub}</small>}
                </>
              );
              return (
                <th
                  key={c.id}
                  className={c.text ? 'is-text' : undefined}
                  aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                  scope="col"
                >
                  {k ? (
                    <button type="button" onClick={() => onSort(k)}>{content}</button>
                  ) : (
                    <span>{content}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading && stocks.length === 0 ? (
            <tr><td colSpan={COLUMNS.length} className="screener-table-empty">Loading stocks…</td></tr>
          ) : stocks.length === 0 ? (
            <tr><td colSpan={COLUMNS.length} className="screener-table-empty">{empty}</td></tr>
          ) : (
            stocks.map((s) => (
              <tr
                key={s.symbol}
                tabIndex={0}
                onClick={() => onOpen(s)}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpen(s); }}
                aria-label={`${s.symbol}: open contracts`}
                style={isLoading ? { opacity: 0.55 } : undefined}
              >
                {COLUMNS.map((c) => (
                  <td
                    key={c.id}
                    className={[c.text ? 'is-text' : '', c.id === 'symbol' ? 'is-sym' : '', c.id === 'name' ? 'is-name' : ''].filter(Boolean).join(' ') || undefined}
                    title={c.id === 'name' ? s.company_name ?? undefined : undefined}
                  >
                    {c.render(s, { isCsp, latest: latestQuotes?.get(s.symbol) ?? null })}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ScreenerTable;
